import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { FEATURE_FLAG_KEYS, hasRole } from '@court-sync/shared';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '@/components/ui';
import { useAuthStore } from '../store/auth';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import subscriptionService from '../services/subscription';
import { fetchSubscriptionQuery } from '../queries/subscription';

const POLL_INTERVAL_MS = 250;
const POLL_MAX_MS = 10_000;

/**
 * Handles the return from Stripe Checkout for platform subscriptions.
 * Stripe redirects here with ?session_id= after a successful checkout.
 * The subscription is activated via the customer.subscription.created webhook;
 * we poll Stripe session status until paid / trialing or timeout, then refresh local state.
 */
const SubscriptionCheckoutSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { enabled: postSubscriptionOnboardingEnabled, loading: flagLoading } = useFeatureFlag(
    FEATURE_FLAG_KEYS.POST_SUBSCRIPTION_ONBOARDING
  );
  const isClubOnlyOwner =
    user &&
    hasRole(user, 'club_owner') &&
    !hasRole(user, 'academy_owner') &&
    !user.academyId;
  const ownerDestination =
    postSubscriptionOnboardingEnabled && !isClubOnlyOwner
      ? '/admin/onboarding'
      : '/admin/dashboard';
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  const [statusMessage, setStatusMessage] = useState(
    'Confirming your subscription…'
  );
  const navigatedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard', { replace: true });
      return undefined;
    }

    if (flagLoading) {
      return undefined;
    }

    let cancelled = false;

    const navigateToDashboard = () => {
      if (navigatedRef.current || cancelled) return;
      navigatedRef.current = true;
      const u = userRef.current;
      const needsEmailVerification = u && !u.isEmailVerified;
      if (needsEmailVerification) {
        const params = new URLSearchParams({ email: u?.email ?? '' });
        params.set('returnUrl', ownerDestination);
        navigate(`/email-verification?${params.toString()}`, {
          state: {
            email: u?.email,
            message:
              'Subscription activated! Please verify your email to complete your account setup.',
            returnUrl: ownerDestination,
            showEmailVerification: true,
          },
        });
      } else {
        navigate(ownerDestination, {
          replace: true,
          state: {
            message: 'Welcome! Your subscription is now active.',
          },
        });
      }
    };

    const run = async () => {
      const start = Date.now();
      let ready = false;

      while (!cancelled && Date.now() - start < POLL_MAX_MS) {
        try {
          const result = await subscriptionService.getCheckoutSessionStatus(sessionId);
          if (result.ready) {
            ready = true;
            break;
          }
        } catch {
          // Network or transient errors — keep polling until timeout
        }
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      }

      if (cancelled) return;

      if (!ready) {
        setStatusMessage(
          'Taking a bit longer than usual. Redirecting to your dashboard — refresh if your plan does not appear.'
        );
      } else {
        setStatusMessage('Your CourtSync subscription is ready. Redirecting to activation…');
      }

      void fetchSubscriptionQuery({ force: true });

      navigateToDashboard();
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [flagLoading, ownerDestination, sessionId, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <Card className="max-w-md w-full">
        <CardContent className="text-center py-10 space-y-4">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Subscription Activated!
          </h1>
          <p className="text-gray-600 dark:text-gray-400">{statusMessage}</p>
          <div className="flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          </div>
          <Button variant="outline" onClick={() => navigate(ownerDestination)} className="w-full">
            {postSubscriptionOnboardingEnabled ? 'Continue Setup Now' : 'Go to Dashboard'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionCheckoutSuccess;
